import React, { useEffect, useId, useState } from "react";
import { OverlayTrigger, Popover } from "react-bootstrap";
import type { OverlayInjectedProps } from "react-bootstrap/esm/Overlay";

// react-bootstrap's OverlayTrigger mis-types children under React 19 — cast to a
// permissive FC (same workaround as InviteScopeSelect / TableActions).
const OverlayTriggerRoot = OverlayTrigger as unknown as React.FC<
  React.PropsWithChildren<Record<string, unknown>>
>;

export interface InfoPopoverProps {
  /** Popover header title */
  title: string;
  /** Body text shown when the popover is open */
  text: string;
  /** Placement relative to the trigger */
  placement?: "top" | "bottom" | "left" | "right";
  /** Optional stable id for aria-controls */
  id?: string;
}

/**
 * Click-triggered info popover (no backdrop). Opens beside the trigger and
 * dismisses on outside tap or the header close button. Mobile-friendly
 * alternative to hover-only InfoTooltip for longer explanatory text.
 */
const InfoPopover: React.FC<InfoPopoverProps> = ({
  title,
  text,
  placement = "bottom",
  id,
}) => {
  const [show, setShow] = useState(false);
  const generatedId = useId();
  const popoverId = id ?? `info-popover-${generatedId.replace(/:/g, "")}`;

  useEffect(() => {
    setShow(false);
  }, [text]);

  const popover = (props: OverlayInjectedProps) => (
    <Popover
      {...props}
      id={popoverId}
      className="shadow-sm"
      style={{ ...props.style, maxWidth: 280 }}
    >
      <Popover.Header className="py-2 px-3 d-flex justify-content-between align-items-center">
        <span className="small fw-semibold mb-0">{title}</span>
        <button
          type="button"
          className="btn-close btn-close-sm"
          aria-label="Close"
          onClick={() => setShow(false)}
        />
      </Popover.Header>
      <Popover.Body className="py-2 px-3 small">{text}</Popover.Body>
    </Popover>
  );

  return (
    <OverlayTriggerRoot
      show={show}
      onToggle={setShow}
      trigger="click"
      placement={placement}
      flip
      rootClose
      overlay={popover}
    >
      <button
        type="button"
        className="btn btn-link btn-sm p-0 text-muted d-inline-flex align-items-center gap-1"
        aria-expanded={show}
        aria-controls={popoverId}
        aria-label={`${title}. ${text}`}
      >
        <span>{title}</span>
        <i className="bi bi-info-circle" style={{ fontSize: "0.95rem" }} aria-hidden />
      </button>
    </OverlayTriggerRoot>
  );
};

export default InfoPopover;
